/**
 * Which of the sales points a finished call covered.
 *
 * Pure, and outside the component for the usual reason: the interesting part is
 * not the layout, it is which of three states this call is in, and only one of
 * them may be drawn as a score.
 *
 *  1. **Nobody has looked.** The co-pilot was never open and the after-call pass
 *     has not reached this call - or cannot, because no key is configured. There
 *     is no answer, and drawing "0 / 6" would invent the worst possible one.
 *  2. **Somebody looked and found nothing covered.** A real, useful answer about
 *     a real call, and identical in the data to case 1 apart from the stamp -
 *     which is the entire reason `checklistReadAt` is a column.
 *  3. **Points were covered.** The ordinary case.
 */
import type { PilotChecklistItem } from "../../../../lib/callPilot";

export interface CoverageRow {
  id: string;
  question: string;
  covered: boolean;
}

export interface Coverage {
  rows: CoverageRow[];
  covered: number;
  total: number;
}

/**
 * The score, or null when this call has no honest one.
 *
 * ⚠ THE DENOMINATOR IS TODAY'S CHECKLIST, NOT THE ONE THE CALL WAS SCORED
 * AGAINST. An id that no longer matches a question is dropped from both halves
 * of the fraction rather than counted: the question was deleted, so "we covered
 * it" is no longer a claim anybody can check, and leaving it in the numerator
 * would produce a call that covered four of three points.
 *
 * Rows follow the checklist's own order rather than putting the misses first.
 * It is the order Settings shows and the order the operator learned, and a list
 * that reorders itself per call is one nobody can scan twice.
 */
export function coverageOf(
  items: readonly PilotChecklistItem[],
  answered: readonly string[] | null | undefined,
  readAt: string | null | undefined,
): Coverage | null {
  if (items.length === 0) return null;

  const marks = answered ?? [];
  // Case 1. Not `marks.length === 0` alone: a call somebody read and found
  // nothing in has the same empty array and does deserve its zero.
  if (!readAt && marks.length === 0) return null;

  const hit = new Set(marks);
  const rows = items.map((item) => ({
    id: item.id,
    question: item.question,
    covered: hit.has(item.id),
  }));

  return {
    rows,
    covered: rows.filter((row) => row.covered).length,
    total: rows.length,
  };
}
